/**
 * Reading-progress bar pinned below the visible header chrome.
 *
 * Uses live header measurements instead of --header-end, which the theme
 * updates only after the hide-menu transition (~300ms) finishes.
 *
 * The bar is `position: fixed`, so nothing written here reflows the page — but
 * the trigger still matters. This used to watch the header's `class` attribute
 * with a MutationObserver, which meant a forced layout read on every class
 * write the header makes while hiding/showing on scroll. Transitions are
 * watched instead: two events per open/close rather than one callback per
 * attribute mutation, and the rAF follow keeps the bar glued to the header
 * while it actually moves.
 */
(() => {
  const progress = document.querySelector('.dimark-progress');
  const bar = document.querySelector('.dimark-progress__bar');
  if (!progress || !bar) return;

  const header = document.querySelector('store-header.header');

  let ticking = false;
  let lastTop = null;
  let lastWidth = null;

  const headerBottom = () => {
    if (header) return header.getBoundingClientRect().bottom;

    const fallback = getComputedStyle(document.documentElement).getPropertyValue('--header-end');
    return parseFloat(fallback) || 0;
  };

  const update = () => {
    ticking = false;

    const top = `${headerBottom().toFixed(1)}px`;
    if (top !== lastTop) {
      lastTop = top;
      progress.style.top = top;
    }

    const max = document.documentElement.scrollHeight - window.innerHeight;
    const pct = max > 0 ? (window.scrollY / max) * 100 : 0;
    const width = `${Math.min(100, Math.max(0, pct)).toFixed(1)}%`;
    if (width !== lastWidth) {
      lastWidth = width;
      bar.style.width = width;
    }
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  document.addEventListener('on:breakpoint-change', onScroll);

  if (header) {
    // Follow the header while it animates (hide/show, product menu band), then
    // stop. Read-only with respect to layout, and self-terminating, so it can
    // never feed back into the header's own scroll-direction logic.
    const FOLLOW_MAX_MS = 800;
    let following = false;

    const follow = (until) => {
      if (following) return;
      following = true;
      const step = () => {
        update();
        if (performance.now() < until) {
          requestAnimationFrame(step);
        } else {
          following = false;
        }
      };
      requestAnimationFrame(step);
    };

    const onTransition = (event) => {
      if (event.target !== header && !header.contains(event.target)) return;
      follow(performance.now() + FOLLOW_MAX_MS);
    };

    header.addEventListener('transitionrun', onTransition);
    header.addEventListener('transitionstart', onTransition);
    header.addEventListener('transitionend', onScroll);
  }

  update();
})();
